import { Rotation } from 'src/types/ViewModel/Rotation';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { PortCall } from 'src/types/ViewModel/PortCall';
import { isManualBerthCall, isManualCall, isManualPortCall, isPortCallAfterFirstVoyageOrcaWebStowageImosSync } from '../../stowageRotationUtils';
import { mapRotationPortCallToPortCall } from './portCallUtils';
import { PortName } from 'src/types/ViewModel/Stowage/Port/PortName';
import { TimeZoneData } from 'src/types/ViewModel/TimeZoneData';

export const mergeRotationWithStowagePlan = (
    rotation: Rotation,
    rotationPortCalls: RotationPortCall[],
    firstVoyageOrcaWebStowageImosSync: number,
    portNameList: PortName[],
    timeZoneData: TimeZoneData
): Set<number> => {
    // Track which port calls are filtered out by stowage integration
    const filteredPortCallIds = new Set<number>();

    rotation.portCallArray = rotation.portCallArray.filter((pc) => {
        const voyage1Num = +pc.voyageNumber1;
        const voyage2Num = pc.voyageNumber2 ? +pc.voyageNumber2 : undefined;

        // Only keep port calls from old system where ALL voyages are BEFORE the cutoff
        // If any voyage is >= cutoff, the data should come from the stowage plan instead
        const isKept = voyage1Num < firstVoyageOrcaWebStowageImosSync && (!voyage2Num || voyage2Num < firstVoyageOrcaWebStowageImosSync);

        if (!isKept) {
            filteredPortCallIds.add(pc.portCallId);
        }

        return isKept;
    });

    rotationPortCalls.forEach((pc) => {
        pc.berthCalls = pc.berthCalls.filter((bc) => !isManualBerthCall(bc));
        pc.berthCalls.forEach((bc) => (bc.cargoOperations = bc.cargoOperations.filter((co) => !isManualCall(co))));
    });

    const filteredRotationPortCalls = rotationPortCalls.filter(
        (pc) => !isManualPortCall(pc) && isPortCallAfterFirstVoyageOrcaWebStowageImosSync(pc, firstVoyageOrcaWebStowageImosSync)
    );

    const seenPortCalls = new Map<string, RotationPortCall>();
    const consolidatedStowagePortCalls: RotationPortCall[] = [];

    for (const currentPc of filteredRotationPortCalls) {
        // Use portCallGuid as unique identifier to prevent incorrect merging
        const key = currentPc.portCallGuid;

        if (seenPortCalls.has(key)) {
            const existing = seenPortCalls.get(key)!;

            if (currentPc.berthCalls && currentPc.berthCalls.length > 0) {
                if (!existing.berthCalls) {
                    existing.berthCalls = [];
                }
                existing.berthCalls = existing.berthCalls.concat(currentPc.berthCalls);
            }
        } else {
            seenPortCalls.set(key, currentPc);
            consolidatedStowagePortCalls.push(currentPc);
        }
    }

    const mergedOrcaPortCallIds = new Set<number>();

    consolidatedStowagePortCalls.forEach((pc) => {
        const latestPortCall = rotation.portCallArray[rotation.portCallArray.length - 1];

        const portCall = mapRotationPortCallToPortCall(pc, portNameList, timeZoneData?.[pc.portId] || [], latestPortCall, mergedOrcaPortCallIds);
        if (portCall) {
            rotation.portCallArray.push(portCall);
        }
    });

    const portCallIdCounts = new Map<number, number>();
    rotation.portCallArray.forEach((pc) => {
        const count = portCallIdCounts.get(pc.portCallId) || 0;
        portCallIdCounts.set(pc.portCallId, count + 1);
    });

    const duplicates = Array.from(portCallIdCounts.entries())
        .filter(([, count]) => count > 1)
        .map(([portCallId, count]) => {
            const calls = rotation.portCallArray.filter((pc) => pc.portCallId === portCallId);
            return {
                portCallId,
                count,
                calls: calls.map((pc) => ({
                    name: pc.name,
                    reportLocation: pc.reportLocation,
                    voyage1: pc.voyageNumber1,
                    voyage2: pc.voyageNumber2,
                })),
            };
        });

    if (duplicates.length > 0) {
        const portCallMap = new Map<string, PortCall>();
        rotation.portCallArray.forEach((pc) => {
            const key = `${pc.portCallId}-${pc.reportLocation}`;
            const existing = portCallMap.get(key);
            if (existing) {
                if (pc.berthCallArray.length > existing.berthCallArray.length) {
                    portCallMap.set(key, pc);
                }
            } else {
                portCallMap.set(key, pc);
            }
        });

        rotation.portCallArray = Array.from(portCallMap.values());

        rotation.portCallArray.forEach((pc) => {
            const seenBerthIds = new Set<number>();
            pc.berthCallArray = pc.berthCallArray.filter((berth) => {
                if (seenBerthIds.has(berth.berthCallId)) {
                    return false;
                }
                seenBerthIds.add(berth.berthCallId);
                return true;
            });
        });
    }

    const finalDeduplicationMap = new Map<string, PortCall>();
    rotation.portCallArray.forEach((pc) => {
        // Include portCallId to prevent merging distinct port calls to the same port
        const key = `${pc.portCallId}-${pc.port?.portId || 'null'}-${pc.reportLocation}-${pc.voyageNumber1}-${pc.voyageNumber2 || 'null'}`;
        const existing = finalDeduplicationMap.get(key);
        if (existing) {
            if (pc.berthCallArray.length > existing.berthCallArray.length) {
                finalDeduplicationMap.set(key, pc);
            }
        } else {
            finalDeduplicationMap.set(key, pc);
        }
    });

    rotation.portCallArray = Array.from(finalDeduplicationMap.values());

    rotation.portCallArray.sort((a, b) => {
        const aMaxVoyage = Math.max(Number(a.voyageNumber1), a.voyageNumber2 ? Number(a.voyageNumber2) : 0);
        const bMaxVoyage = Math.max(Number(b.voyageNumber1), b.voyageNumber2 ? Number(b.voyageNumber2) : 0);

        if (aMaxVoyage !== bMaxVoyage) {
            return aMaxVoyage - bMaxVoyage;
        }

        const aVoyage1 = Number(a.voyageNumber1);
        const bVoyage1 = Number(b.voyageNumber1);
        return aVoyage1 - bVoyage1;
    });

    rotation.portCallArray.forEach((pc) => (pc.berthCallArray = pc.berthCallArray.filter((bc) => bc.berthId > 0)));

    return filteredPortCallIds;
};
